import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';

import { commafy, decommafy, swal, toEnglishDigits, typeChecker } from '../../../utils/methods';
import { _post } from '../../../utils/network';
import { contentLevelOptions, coursePriceUnits, courseTypeOptions, fetchCourseCategoryOptions, getCourseType, isHttpUrl, parseCourseCapacity, toShamsiDate } from '../courseUtils';
import { emptyInPersonCoursePayload, getInPersonCourseDetails, parseInPersonCourseDetails } from '../inPersonCourseDetails';

import Breadcrumb from '../../../components/Breadcrumb/Breadcrumb.component';
import FormField from '../../../components/Form/FormField/FormField.component';
import LoadingButton from '../../../components/LoadingButton/LoadingButton.component';
import ContentEditor from '../../../components/Form/ContentEditor/ContentEditor.component';
import InPersonCourseFields from './InPersonCourseFields.component';

const INTRO_VIDEO_MAX_SIZE_MB = 200;
const EMPTY_VIDEO = { title: '', url: '', description: '', is_free: false, is_active: true };

const stripHtml = html => (html || '').replace(/<[^>]*>/g, ' ').replace(/&nbsp;/g, ' ').replace(/\s+/g, ' ').trim();

const newVideoId = () => window.crypto?.randomUUID?.() || `video-${Date.now()}-${Math.random().toString(16).slice(2)}`;

const CoursesCreate = ({ componentType='create' }) => {
	const { t } = useTranslation();
	const { id } = useParams();
	const navigate = useNavigate();
	const location = useLocation();
	const videoEditorRef = useRef(null);
	const [formKey, setFormKey] = useState(0);
	const [saveButtonIsLoading, setSaveButtonIsLoading] = useState(false);
	const [deleteButtonIsLoading, setDeleteButtonIsLoading] = useState(false);
	const [course, setCourse] = useState({});
	const [courseCategories, setCourseCategories] = useState([]);
	const [description, setDescription] = useState('');
	const [isFree, setIsFree] = useState(false);
	const [courseType, setCourseType] = useState('online');
	const [capacity, setCapacity] = useState('');
	const [introVideo, setIntroVideo] = useState('');
	const [inPersonDetails, setInPersonDetails] = useState(getInPersonCourseDetails);
	const [videos, setVideos] = useState([]);
	const [videoDraft, setVideoDraft] = useState(EMPTY_VIDEO);
	const [editingVideoId, setEditingVideoId] = useState(null);

	const breadcrumbItems = [
		{ title: t('dashboard'), link: '/dashboard' },
		{ title: t('courses'), link: '/courses-list' },
		{ title: t(componentType) }
	];
	const confirmSwal = statements => {
		swal(() => statements(), {
			text: t('are_you_sure_to_do_this'),
			confirmButtonText: t('yes'),
			cancelButtonText: t('no')
		});
	};

	const clearVideoEditor = () => {
		setVideoDraft(EMPTY_VIDEO);
		setEditingVideoId(null);
	};

	const resetCreateForm = () => {
		setCourse({});
		setDescription('');
		setIsFree(false);
		setCourseType('online');
		setCapacity('');
		setIntroVideo('');
		setInPersonDetails(getInPersonCourseDetails());
		setVideos([]);
		clearVideoEditor();
		setFormKey(key => key + 1);
	};

	const videoDraftIsDirty = editingVideoId !== null || Object.keys(EMPTY_VIDEO).some(key => videoDraft[key] !== EMPTY_VIDEO[key]);

	const saveVideo = () => {
		const title = videoDraft.title.trim();
		const url = videoDraft.url.trim();
		if(!title)
			return toast.warn(t('course_video_title') + ' ' + t('cant_be_empty'));
		if(!isHttpUrl(url))
			return toast.warn(t('course_video_link_invalid'));
		const video = { title, url, description: videoDraft.description.trim(), is_free: videoDraft.is_free, is_active: videoDraft.is_active };
		if(editingVideoId !== null)
			setVideos(videos.map(item => item.id === editingVideoId ? { ...item, ...video } : item));
		else
			setVideos([...videos, { id: newVideoId(), ...video }]);
		clearVideoEditor();
	};

	const editVideo = videoId => {
		const video = videos.find(item => item.id === videoId);
		if(!video)
			return;
		setVideoDraft({ title: video.title, url: video.url, description: video.description || '', is_free: !!video.is_free, is_active: video.is_active !== false });
		setEditingVideoId(videoId);
		videoEditorRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
	};

	const deleteVideo = videoId => {
		confirmSwal(() => {
			setVideos(current => current.filter(video => video.id !== videoId));
			if(editingVideoId === videoId)
				clearVideoEditor();
		});
	};

	const toggleVideoActive = videoId => {
		setVideos(current => current.map(video => video.id === videoId ? { ...video, is_active: video.is_active === false } : video));
	};

	const moveVideo = (index, step) => {
		const target = index + step;
		if(target < 0 || target >= videos.length)
			return;
		const reordered = [...videos];
		[reordered[index], reordered[target]] = [reordered[target], reordered[index]];
		setVideos(reordered);
	};

	const saveCourse = event => {
		event.preventDefault();
		if(saveButtonIsLoading)
			return;
		const form = event.currentTarget;
		const getField = name => form.elements.namedItem(name);
		const parsedCapacity = courseType === 'in_person' ? parseCourseCapacity(capacity) : null;
		if(courseType === 'in_person' && parsedCapacity === null)
			return toast.warn(t('course_capacity_invalid'));
		if(courseType === 'in_person' && parsedCapacity < (course?.enrollments_count ?? 0))
			return toast.warn(t('course_capacity_below_enrollments'));
		const attendance = courseType === 'in_person' ? parseInPersonCourseDetails(inPersonDetails) : { details: emptyInPersonCoursePayload() };
		if(attendance.error)
			return toast.warn(t(attendance.error));
		const title = getField('courseTitle').value.trim();
		if(!typeChecker(title, 'title'))
			return toast.warn(t('title') + ' ' + t('must_have_at_least_3_characters'));
		const categoryId = getField('courseCategory').value;
		if(!typeChecker(categoryId, 'uuid'))
			return toast.warn(t('category') + ' ' + t('cant_be_empty'));
		const brief = getField('courseBrief').value.trim();
		if(!typeChecker(brief, 'title'))
			return toast.warn(t('brief') + ' ' + t('must_have_at_least_3_characters'));
		if(stripHtml(description).length < 3)
			return toast.warn(t('description') + ' ' + t('must_have_at_least_3_characters'));
		const introVideoUrl = introVideo.trim().replace(/^http:\/\//i, 'https://');
		const image = document.querySelector('#courseImage-upload-list img')?.getAttribute('src')?.trim() || '';
		if(!typeChecker(image, 'image'))
			return toast.warn(t('course_cover') + ' ' + t('cant_be_empty'));
		let price = 0;
		let priceUnit = null;
		if(!isFree){
			priceUnit = getField('coursePriceUnit').value;
			if(!priceUnit)
				return toast.warn(t('price_unit') + ' ' + t('cant_be_empty'));
			price = parseInt(toEnglishDigits(decommafy(getField('coursePrice').value.trim())));
			if(!typeChecker(price, 'number') || price <= 0)
				return toast.warn(t('enter_price_correctly'));
		}
		if(courseType === 'online' && videoDraftIsDirty)
			return toast.warn(t('course_unsaved_video'));

		const body = {
			id,
			title,
			category_id: categoryId,
			brief,
			level: getField('courseLevel').value || 'beginner',
			description,
			images: { ...(course?.images || {}), image },
			intro_video: introVideoUrl || null,
			course_type: courseType,
			capacity: parsedCapacity,
			...attendance.details,
			videos: courseType === 'in_person' ? [] : videos,
			is_free: isFree,
			price,
			price_unit: priceUnit,
			is_active: getField('courseIsActive').checked,
			is_special: getField('courseIsSpecial').checked,
			more: course?.more || {}
		};
		confirmSwal(async () => {
			setSaveButtonIsLoading(true);
			const response = await _post(`/courses/admin/${componentType === 'create' ? 'create' : 'update'}Course`, body);
			setSaveButtonIsLoading(false);
			switch(response.code){
				case 200:
					toast.success(response[t('api_message')]);
					if(componentType === 'create')
						resetCreateForm();
					break;
				default:
					toast.error(response[t('api_message')]);
			}
		});
	};

	const deleteCourse = () => {
		if(deleteButtonIsLoading)
			return;
		confirmSwal(async () => {
			setDeleteButtonIsLoading(true);
			const response = await _post('/courses/admin/deleteCourse', { id });
			setDeleteButtonIsLoading(false);
			switch(response.code){
				case 200:
					toast.success(response[t('api_message')]);
					navigate('/courses-list');
					break;
				default:
					toast.error(response[t('api_message')]);
			}
		});
	};

	const getCourseCategories = async () => {
		const { options, response } = await fetchCourseCategoryOptions();
		if(response.code !== 200)
			return toast.error(response[t('api_message')]);
		setCourseCategories(options);
	};

	const getCourse = async () => {
		const response = await _post('/courses/admin/getOneCourse', { id });
		switch(response.code){
			case 200: {
				const loaded = response.data.course;
				setCourse(loaded);
				setDescription(loaded.description || '');
				setIsFree(!!loaded.is_free);
				setCourseType(getCourseType(loaded));
				setCapacity(loaded.capacity == null ? '' : String(loaded.capacity));
				setIntroVideo(loaded.intro_video || '');
				setInPersonDetails(getInPersonCourseDetails(loaded));
				setVideos(Array.isArray(loaded.videos) ? loaded.videos : []);
				clearVideoEditor();
				setFormKey(key => key + 1);
				break;
			}
			default:
				toast.error(response[t('api_message')]);
		}
	};

	useEffect(() => {
		getCourseCategories();
		if(componentType === 'edit')
			getCourse();
	}, []);
	useEffect(() => {
		if(componentType === 'create')
			resetCreateForm();
	}, [location]);

	return (
		<>
			<Breadcrumb items={breadcrumbItems} />
			<div className="card mb-3">
				<div className="card-header d-flex justify-content-between align-items-center">
					<h5 className="mb-0">{t(componentType)} {t('course')}</h5>
					{componentType === 'edit' &&
						<Link className="btn btn-sm btn-outline-primary" to={`/course-enrollments-list?course_id=${id}`}>
							<i className="fa fa-users"></i> {t('course_enrollments')} ({commafy(course?.enrollments_count ?? 0)})
						</Link>
					}
				</div>
				<div className="card-body bg-light">
					<form onSubmit={saveCourse} key={formKey}>
						<div className="row gx-2">
							<FormField fieldKey="courseTitle" title={t('title')} value={course?.title} required />
							<FormField type="select" fieldKey="courseCategory" title={t('category')} value={course?.category_id} selectItems={courseCategories} required />
							<div className="col-md-6 mb-3">
								<label className="form-label" htmlFor="courseType">{t('course_type')} <span className="text-danger"> *</span></label>
								<select className="form-select" id="courseType" name="courseType" value={courseType} disabled={componentType === 'edit' && course?.enrollments_count > 0} onChange={event => setCourseType(event.target.value)}>
									{courseTypeOptions(t).map(option => <option key={option.value} value={option.value}>{option.key}</option>)}
								</select>
								{componentType === 'edit' && course?.enrollments_count > 0 && <small className="text-muted">{t('course_type_enrolled_hint')}</small>}
							</div>
							<div className="col-md-6 mb-3">
								{courseType === 'in_person' && <>
									<label className="form-label" htmlFor="courseCapacity">{t('course_capacity')} <span className="text-danger"> *</span></label>
									<input className="form-control ltr" id="courseCapacity" name="courseCapacity" type="text" inputMode="numeric" value={capacity} onChange={event => setCapacity(event.target.value)} />
									{componentType === 'edit' && <div className="small text-muted mt-1">{t('course_enrollments')}: {commafy(course?.enrollments_count ?? 0)}</div>}
								</>}
							</div>
							{courseType === 'in_person' && <InPersonCourseFields details={inPersonDetails} onChange={(field, value) => setInPersonDetails(current => ({ ...current, [field]: value }))} />}
							<FormField type="toggle" fieldKey="courseIsActive" title={t('active')} value={course?.is_active} required />
							<FormField type="toggle" fieldKey="courseIsSpecial" title={t('special')} value={course?.is_special} required />
							<div className="col-md-3 mb-3">
								<label className="form-check-label" htmlFor="courseIsFree">{t('course_is_free')}</label>
								<div className="form-check form-switch">
									<input className="form-check-input" id="courseIsFree" name="courseIsFree" type="checkbox" checked={isFree} onChange={event => setIsFree(event.target.checked)} />
								</div>
							</div>
							{isFree ?
								<div className="col-md-6 mb-3 d-flex align-items-end">
									<div className="alert alert-info py-2 mb-0 w-100">{t(courseType === 'in_person' ? 'course_in_person_free_hint' : 'course_free_hint')}</div>
								</div>
							:
								<>
									<FormField type="select" fieldKey="coursePriceUnit" title={t('price_unit')} value={course?.price_unit} selectItems={coursePriceUnits(t)} required rtl />
									<FormField type="number" fieldKey="coursePrice" title={t('price')} value={course?.price ? commafy(course.price) : ''} required ltr />
								</>
							}
							<FormField type="text" fieldKey="courseBrief" title={t('brief')} value={course?.brief} required />
							<FormField type="select" fieldKey="courseLevel" title={t('level')} value={course?.level || 'beginner'} selectItems={contentLevelOptions(t)} required />
							<FormField type="image" fieldKey="courseImage" title={t('course_cover')} value={course?.images?.image} required fileMaxSize={2} imageAspectRatio="16 / 9" />
							<FormField type="video" fieldKey="courseIntroVideo" title={t('course_intro_video')} tip={t('course_intro_video_tip')} value={introVideo} fileMaxSize={INTRO_VIDEO_MAX_SIZE_MB} onUploadChange={value => setIntroVideo(value || '')} />
							<div className="col-12 mb-3">
								<ContentEditor
									key={`course-description-${formKey}`}
									fieldKey="courseDescription"
									title={t('description')}
									required
									initContent={description}
									onChange={event => setDescription(event.editor.getData())}
								/>
							</div>
							{componentType === 'edit' &&
								<>
									<FormField fieldKey="courseCreatedAt" title={t('creation_date')} value={toShamsiDate(course?.created_at)} disabled ltr />
									<FormField fieldKey="courseUpdatedAt" title={t('update_date')} value={toShamsiDate(course?.updated_at)} disabled ltr />
								</>
							}

							{courseType === 'in_person' ?
								<div className="col-12"><div className="alert alert-info mb-0">{t('course_in_person_lessons_hint')}</div></div>
							:
							<>
							<div className="col-12">
								<hr className="my-4" />
							</div>

							<div className="col-12 mb-3" ref={videoEditorRef}>
								<h6 className="mb-1">
									{t('course_videos')} {editingVideoId ? <small className="text-warning">({t('editing')})</small> : ''}
								</h6>
								<p className="text-muted small mb-3">{t('course_videos_tip')}</p>
								<div className="row gx-2">
									<div className="col-md-6 mb-3">
										<label className="form-label" htmlFor="courseVideoTitle">{t('course_video_title')} <span className="text-danger"> *</span></label>
										<input className="form-control" id="courseVideoTitle" type="text" value={videoDraft.title} placeholder={t('course_video_title')} onChange={event => setVideoDraft({ ...videoDraft, title: event.target.value })} />
									</div>
									<div className="col-md-6 mb-3">
										<label className="form-label" htmlFor="courseVideoUrl">{t('course_video_link')} <span className="text-danger"> *</span></label>
										<input className="form-control ltr" id="courseVideoUrl" type="url" value={videoDraft.url} placeholder="https://" onChange={event => setVideoDraft({ ...videoDraft, url: event.target.value })} />
									</div>
									<div className="col-md-6 mb-3">
										<label className="form-label" htmlFor="courseVideoDescription">{t('description')}</label>
										<textarea className="form-control" id="courseVideoDescription" rows="2" value={videoDraft.description} placeholder={t('description')} onChange={event => setVideoDraft({ ...videoDraft, description: event.target.value })}></textarea>
									</div>
									<div className="col-md-3 mb-3">
										<label className="form-check-label" htmlFor="courseVideoIsFree">{t('course_video_is_free')}</label>
										<div className="form-check form-switch">
											<input className="form-check-input" id="courseVideoIsFree" type="checkbox" checked={videoDraft.is_free} onChange={event => setVideoDraft({ ...videoDraft, is_free: event.target.checked })} />
										</div>
									</div>
									<div className="col-md-3 mb-3">
										<label className="form-check-label" htmlFor="courseVideoIsActive">{t('course_video_is_active')}</label>
										<div className="form-check form-switch">
											<input className="form-check-input" id="courseVideoIsActive" type="checkbox" checked={videoDraft.is_active} onChange={event => setVideoDraft({ ...videoDraft, is_active: event.target.checked })} />
										</div>
									</div>
								</div>
								<div>
									<button type="button" className={`btn ${editingVideoId ? 'btn-primary' : 'btn-success'} me-2`} onClick={saveVideo}>
										<i className={`fa ${editingVideoId ? 'fa-save' : 'fa-plus'}`}></i> {editingVideoId ? t('save') : t('course_add_video')}
									</button>
									{videoDraftIsDirty &&
										<button type="button" className="btn btn-secondary" onClick={clearVideoEditor}>
											<i className="fa fa-times"></i> {t('cancel')}
										</button>
									}
								</div>
							</div>

							{videos.length > 0 &&
								<div className="col-12 mb-3">
									<label className="form-label">{t('course_added_videos')} ({videos.length})</label>
									<div className="list-group">
										{videos.map((video, index) =>
											<div key={video.id} className={`list-group-item${editingVideoId === video.id ? ' list-group-item-warning' : video.is_active === false ? ' bg-light text-muted' : ''}`}>
												<div className="d-flex justify-content-between align-items-start gap-2">
													<div className="flex-grow-1 overflow-hidden">
														<h6 className="mb-1">
															{index + 1}. {video.title}{' '}
															<span className={`badge rounded-pill badge-subtle-${isFree || video.is_free ? 'success' : 'warning'}`}>
																{isFree || video.is_free ? t('free') : t('paid')}
															</span>{' '}
															{video.is_active === false &&
																<span className="badge rounded-pill badge-subtle-secondary">{t('course_video_hidden')}</span>
															}
														</h6>
														<a className="d-block small text-truncate ltr text-start" href={video.url} target="_blank" rel="noopener noreferrer">{video.url}</a>
														{video.description ? <p className="mb-0 text-muted small text-truncate">{video.description}</p> : null}
													</div>
													<div className="d-flex flex-shrink-0 gap-1">
														<button type="button" className="btn btn-sm btn-outline-secondary" disabled={index === 0} onClick={() => moveVideo(index, -1)}>
															<i className="fa fa-arrow-up"></i>
														</button>
														<button type="button" className="btn btn-sm btn-outline-secondary" disabled={index === videos.length - 1} onClick={() => moveVideo(index, 1)}>
															<i className="fa fa-arrow-down"></i>
														</button>
														<button type="button" className={`btn btn-sm ${video.is_active === false ? 'btn-outline-success' : 'btn-outline-secondary'}`} title={video.is_active === false ? t('course_video_show') : t('course_video_hide')} onClick={() => toggleVideoActive(video.id)}>
															<i className={`fa ${video.is_active === false ? 'fa-eye' : 'fa-eye-slash'}`}></i>
														</button>
														<button type="button" className="btn btn-sm btn-warning" onClick={() => editVideo(video.id)}>
															<i className="fa fa-edit"></i> {editingVideoId === video.id ? t('editing') : t('edit')}
														</button>
														<button type="button" className="btn btn-sm btn-danger" onClick={() => deleteVideo(video.id)}>
															<i className="fa fa-trash"></i> {t('delete')}
														</button>
													</div>
												</div>
											</div>
										)}
									</div>
								</div>
							}
							</>
							}
						</div>
						<div>
							<LoadingButton className="btn btn-primary d-inline-block mt-3 me-2" type="submit" name={componentType} isLoading={saveButtonIsLoading}>
								<i className="fa fa-save"></i> {t(componentType === 'create' ? 'create' : 'update')}
							</LoadingButton>
							{componentType === 'edit' &&
								<LoadingButton className="btn btn-danger d-inline-block mt-3" onClick={deleteCourse} type="button" name="delete" isLoading={deleteButtonIsLoading}>
									<i className="fa fa-trash"></i> {t('delete')}
								</LoadingButton>
							}
						</div>
					</form>
				</div>
			</div>
		</>
	);
};

export default CoursesCreate;
